import { apiCall } from "@/api/client";
import type {
  BacklogItem,
  BacklogMeta,
  CreateBacklogItemInput,
  ListBacklogResponse,
  UpdateBacklogItemInput,
} from "@/types/api";

/**
 * Backlog API — a thin façade over the org-level GitHub Project.
 *
 * Two write paths on purpose, mirroring the backend:
 *
 *   - {@link updateBacklogItem} writes *Project* state (Priority, Area,
 *     Status, ordering). Addressed by the Project item id.
 *   - {@link updateBacklogContent} writes the *issue* behind the item (title,
 *     body, open/closed). Same id, different resource — the server resolves
 *     the issue itself rather than trusting the client to name it.
 *
 * Responses arrive camelCased by the shared `json_response` helper, so the
 * types below are the wire shape as-is; no conversion layer here (unlike the
 * catalog's opaque `payload`, see payloadCase.ts).
 */

/** Backend route for one item — exported so the router and client agree. */
export function backlogItemPath(itemId: string): string {
  return `/backlog/items/${encodeURIComponent(itemId)}`;
}

export function getBacklogMeta(token: string) {
  return apiCall<BacklogMeta>("/backlog/meta", { token });
}

export function listBacklogItems(token: string, cursor?: string) {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return apiCall<ListBacklogResponse>(`/backlog/items${qs}`, { token });
}

export function createBacklogItem(token: string, input: CreateBacklogItemInput) {
  return apiCall<BacklogItem>("/backlog/items", {
    method: "POST",
    body: input,
    token,
  });
}

export function updateBacklogItem(
  token: string,
  itemId: string,
  input: UpdateBacklogItemInput,
) {
  return apiCall<{ itemId: string }>(backlogItemPath(itemId), {
    method: "PATCH",
    body: input,
    token,
  });
}

export function updateBacklogContent(
  token: string,
  itemId: string,
  input: { title?: string; body?: string; state?: "open" | "closed" },
) {
  return apiCall<{ itemId: string }>(`${backlogItemPath(itemId)}/content`, {
    method: "PATCH",
    body: input,
    token,
  });
}

/**
 * Hand the item to the development agent, and take it back.
 *
 * These drive a label on the underlying issue, which is what starts
 * `agent-task.yml` in the dataplane. A route of its own rather than a field on
 * {@link updateBacklogItem}, because it is the only backlog call with an effect
 * outside the control plane — it starts a coding agent.
 *
 * The server refuses a closed issue, a repository that runs no agent, and an
 * item already handed over (409): GitHub only fires on a label *transition*, so
 * re-applying would start nothing while looking like a fresh run. Recall first.
 */
export function handToAgent(token: string, itemId: string) {
  return apiCall<{ itemId: string; labels: string[] }>(
    `${backlogItemPath(itemId)}/agent`,
    { method: "POST", token },
  );
}

export function recallFromAgent(token: string, itemId: string) {
  return apiCall<{ itemId: string; labels: string[] }>(
    `${backlogItemPath(itemId)}/agent`,
    { method: "DELETE", token },
  );
}

/**
 * Walk every page of the Project.
 *
 * The table sorts and filters client-side over the whole backlog, which only
 * works because the whole backlog is small (≈120 items, 3 pages). If it ever
 * approaches the Project's 50 000-item ceiling this has to become server-side
 * filtering — the loop is bounded here so that day fails loudly rather than
 * hanging the browser.
 */
const MAX_PAGES = 40;

export async function listAllBacklogItems(token: string): Promise<BacklogItem[]> {
  const items: BacklogItem[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await listBacklogItems(token, cursor);
    items.push(...response.items);
    if (!response.nextCursor) return items;
    cursor = response.nextCursor;
  }
  throw new Error(
    `Backlog exceeds ${MAX_PAGES} pages — the table needs server-side filtering`,
  );
}
